import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  IconButton,
  TextField,
  Divider,
  Tooltip,
  alpha,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import { ContainedButton, OutlinedButton } from "../../Common/Buttons";
import { useSelector } from "react-redux";

export default function TransferModal({ open, onClose, walletData }) {
  const { btncolor } = useSelector((state) => state);
  const [from, setFrom] = useState("SPOT WALLET");
  const [to, setTo] = useState("P2P WALLET");
  const [amount, setAmount] = useState("");

  const handleSwap = () => {
    setFrom(to);
    setTo(from);
  };

  const handleConfirm = () => {
    console.log(
      `Transferring ${amount} ${walletData.coin} from ${from} to ${to}`
    );
    onClose();
  };

  if (!walletData) return null;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{ sx: dialogPaper }}
    >
      <DialogTitle sx={dialogTitleStyle}>
        Fund Transfer
        <IconButton onClick={onClose} size="small" sx={dialogCloseBtn}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <Divider sx={{ borderColor: "rgba(125,125,125,0.15)" }} />

      <DialogContent sx={dialogContentStyle}>
        <Typography sx={infoText}>
          Internal transfers between wallets are free of charge.
        </Typography>

        <Box sx={{ mt: 2.5, display: "flex", flexDirection: "column", gap: 1.5 }}>
          {/* From */}
          <Box>
            <Typography sx={fieldLabel}>From</Typography>
            <Box sx={displayBox}>
              <Typography sx={displayText}>{from}</Typography>
            </Box>
          </Box>

          {/* Swap */}
          <Box sx={{ display: "flex", justifyContent: "center", my: 0.25 }}>
            <Tooltip title="Swap direction" arrow>
              <IconButton
                onClick={handleSwap}
                size="small"
                sx={{
                  backgroundColor: alpha(btncolor || "#1976d2", 0.1),
                  border: `1px solid ${alpha(btncolor || "#1976d2", 0.25)}`,
                  color: btncolor,
                  "&:hover": {
                    backgroundColor: alpha(btncolor || "#1976d2", 0.2),
                  },
                }}
              >
                <SwapHorizIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>

          {/* To */}
          <Box>
            <Typography sx={fieldLabel}>To</Typography>
            <Box sx={displayBox}>
              <Typography sx={displayText}>{to}</Typography>
            </Box>
          </Box>

          {/* Coin */}
          <Box>
            <Typography sx={fieldLabel}>Coin</Typography>
            <Box sx={displayBox}>
              <Typography sx={displayText}>{walletData.coin}</Typography>
            </Box>
          </Box>

          {/* Amount */}
          <Box>
            <Typography sx={fieldLabel}>Amount</Typography>
            <TextField
              fullWidth
              placeholder="0.00"
              variant="outlined"
              type="number"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              sx={amountField}
            />
          </Box>
        </Box>
      </DialogContent>

      <Divider sx={{ borderColor: "rgba(125,125,125,0.15)" }} />
      <DialogActions sx={dialogActionsStyle}>
        <OutlinedButton
          onClick={onClose}
          variant="outlined"
          sx={{ minWidth: 100, height: 42, borderRadius: "10px", textTransform: "none", fontWeight: 600 }}
        >
          Cancel
        </OutlinedButton>
        <ContainedButton
          fullWidth={false}
          variant="contained"
          onClick={handleConfirm}
          sx={{ minWidth: 140, height: 42, borderRadius: "10px", textTransform: "none", fontWeight: 600 }}
        >
          Confirm Transfer
        </ContainedButton>
      </DialogActions>
    </Dialog>
  );
}

// ── Styles ────────────────────────────────────────────────────────────────────

const dialogPaper = {
  borderRadius: "16px",
  backgroundColor: "var(--popupbody-color)",
  color: "var(--text-color)",
  boxShadow: "0 24px 60px rgba(0,0,0,0.28)",
};

const dialogTitleStyle = {
  textAlign: "center",
  fontWeight: 700,
  fontSize: "1.1rem",
  py: 2.25,
  color: "var(--text-color)",
  backgroundColor: "var(--popupheader-color)",
  position: "relative",
};

const dialogCloseBtn = {
  position: "absolute",
  right: 12,
  top: "50%",
  transform: "translateY(-50%)",
  color: "var(--text-color)",
  opacity: 0.6,
};

const dialogContentStyle = {
  pt: 2.5,
  pb: 2,
  px: { xs: 2.5, md: 3 },
  backgroundColor: "var(--popupbody-color)",
};

const dialogActionsStyle = {
  px: 3,
  py: 2,
  backgroundColor: "var(--popupbody-color)",
  justifyContent: "flex-end",
  gap: 1,
};

const infoText = {
  fontSize: "0.82rem",
  color: "var(--text-color)",
  opacity: 0.65,
  lineHeight: 1.6,
};

const fieldLabel = {
  fontSize: "0.72rem",
  fontWeight: 700,
  textTransform: "uppercase",
  letterSpacing: "0.06em",
  color: "var(--text-color)",
  opacity: 0.55,
  mb: 0.75,
};

const displayBox = {
  backgroundColor: "var(--body-color-less2)",
  border: "1px solid rgba(125,125,125,0.2)",
  borderRadius: "10px",
  px: 2,
  py: 1.25,
};

const displayText = {
  fontSize: "0.88rem",
  fontWeight: 600,
  color: "var(--text-color)",
};

const amountField = {
  "& .MuiInputBase-root": {
    height: "46px",
    borderRadius: "10px",
    backgroundColor: "var(--body-color-less2)",
    color: "var(--text-color)",
  },
  "& .MuiInputBase-input": { color: "var(--text-color)" },
  "& .MuiOutlinedInput-notchedOutline": {
    borderColor: "rgba(125,125,125,0.25)",
  },
  "& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline": {
    borderColor: "rgba(125,125,125,0.45)",
  },
  "& .MuiInputLabel-root": { display: "none" },
  "& legend": { display: "none" },
  "& fieldset": { top: 0 },
};
